/**
 * In-app help content for the Help & Feedback page. Keep answers short and
 * action-first (≤2 sentences), with an optional `to` deep-link. When a capability
 * is added or changed, update the relevant entries here in the same change
 * (see Claude.md → "Help documentation").
 */
import {
  Rocket,
  ChartColumn,
  FolderOpen,
  Lightbulb,
  GitPullRequest,
  ShieldCheck,
  Plug,
  Users,
  Settings,
  Lock,
  type LucideIcon,
} from 'lucide-react';

export interface FaqItem {
  q: string;
  a: string;
  to?: string;
}
export interface FaqCategory {
  id: string;
  title: string;
  icon: LucideIcon;
  items: FaqItem[];
}

export const FAQ_CATEGORIES: FaqCategory[] = [
  {
    id: 'getting-started',
    title: 'Getting started',
    icon: Rocket,
    items: [
      {
        q: 'How do I connect my first cloud account?',
        a: 'Open a domain → Integrations → “Connect a source” → AWS or GCP, and follow the guided read-only setup. For AWS, one click launches the CloudFormation stack (sign in to the target AWS account first for the smoothest flow); if you’d rather do it by hand, expand “Prefer to set it up manually?” for the template URL, the ExternalId/account-id values, and copy-paste Console or AWS-CLI steps.',
        to: '/domains',
      },
      {
        q: 'I connected an account and it says “Verifying” — is something wrong?',
        a: 'No — that means the connection was accepted but we couldn’t confirm access on the spot. Right after you run the setup script, cloud IAM changes (especially on GCP/Azure) can take a few minutes to propagate. Lizrd re-checks automatically on the next sync and flips it to Connected once access works; if it still can’t get in after that, it switches to “Needs attention” with the reason. If a connection genuinely can’t be verified (e.g. wrong details), the setup form rejects it up front rather than leaving a broken source behind.',
      },
      {
        q: 'What connections does Lizrd need to get started?',
        a: 'Two, both read-only: a cloud account (so Lizrd sees what you run and what it costs) and a code base (so it understands how those resources are used and can pinpoint the exact fix). Onboarding asks for both before you continue.',
        to: '/domains',
      },
      {
        q: 'When will I see recommendations?',
        a: 'A newly connected account syncs right away; a full first pass (resources, cost, and recommendations) can take up to an hour. The dashboard and Optimizations board fill in automatically, and Lizrd re-syncs every hour after that.',
      },
      {
        q: 'What access does Lizrd need?',
        a: 'Read-only, least-privilege access to your cloud and code. We never change your infrastructure, and you can revoke access at any time.',
      },
      {
        q: 'Which cloud regions does Lizrd scan?',
        a: 'All of them. For AWS, Lizrd scans every region enabled on the account (or just the regions you choose at connect time). GCP and Azure are scanned account-wide in a single pass, so every region is covered automatically. The integration’s detail panel and the sync pipeline show exactly which regions were covered on the last sync. In the rare case an AWS account has more than 30 active regions, Lizrd scans the first 30 and shows a warning — and deliberately won’t mark anything in the uncovered regions as deleted.',
      },
    ],
  },
  {
    id: 'dashboard',
    title: 'Dashboard',
    icon: ChartColumn,
    items: [
      {
        q: 'What does “Recoverable / month” mean?',
        a: 'The total estimated monthly saving across every non-terminal recommendation (open, accepted, or in validation) in all your domains.',
        to: '/',
      },
      {
        q: 'What is the Domains leaderboard?',
        a: 'Your domains ranked by opportunity, realized savings, utilization, or most-improved — with badges for the leaders. Use “Rank by” to switch.',
      },
      {
        q: 'What is Optimization Status?',
        a: 'The recommendation funnel (produced → accepted → executed → validated) plus a live backlog chart, filterable by domain and time window.',
      },
      {
        q: 'What is “Recoverable by category”?',
        a: 'Your recoverable savings split by type of waste (right-sizing, idle, storage tiering, modernization, …) so you can see where to focus. Expand a category to drill into the domains behind it and the exact recommendations; click a recommendation to open its detail page.',
      },
    ],
  },
  {
    id: 'domains',
    title: 'Domains',
    icon: FolderOpen,
    items: [
      {
        q: 'What is a Domain?',
        a: 'A slice of your world you manage as one — a product line, a single product, or one team’s architecture (e.g. Payroll). You choose the granularity; most teams start with one and add more (up to 10).',
        to: '/domains',
      },
      {
        q: 'How do I switch between domains?',
        a: 'Click “Domains” in the left sidebar — your domains appear beneath it; pick one to open its dashboard.',
      },
      {
        q: 'What is the Explorer?',
        a: 'A view of the resources Lizrd discovered and their monthly cost — as a breakdown, tree map, or trend, plus the architecture graph.',
      },
      {
        q: 'Why does the Explorer say it’s “showing N of M” resources?',
        a: 'For very large domains the Explorer lists a capped set of resources at once to stay fast, so it tells you when it’s showing a subset. Your cost figures are complete regardless — the total, the breakdown by cloud and category, and the tree map are all computed over every resource (the biggest tile really is your biggest spend). Only the detailed Cost Drivers list is limited to the shown subset; narrow with a type filter or search to see the rest. Recommendations and totals are unaffected.',
      },
      {
        q: 'Estimated vs. billed costs: when do I see which?',
        a: 'A domain’s cost — on its dashboard and in the Domains list — shows your cloud provider’s real billed amount whenever we can read it: the charges so far this month (month-to-date), with the projected full-month run rate beside it. Billed dollars are shown plainly; only estimates carry an “Est.” tag with a confidence rating. The Explorer cost breakdown, tree map and trend are calibrated to that billed spend per cloud service (so they add up to the same monthly total shown up top); resources we can’t match to billing keep an “Est.” tag. Billed spend that isn’t tied to a specific resource — tax, data transfer, monitoring, and platform services we don’t model individually — rolls into an “Other services” line you can expand to see the exact billed-service line items.',
      },
    ],
  },
  {
    id: 'recommendations',
    title: 'Optimizations',
    icon: Lightbulb,
    items: [
      {
        q: 'How do I act on a recommendation?',
        a: 'Open it → Accept → apply the change (use the generated steps/diff) → Mark executed. Lizrd validates the realized saving automatically on the next syncs.',
      },
      {
        q: 'Where do I see the full detail for a recommendation?',
        a: 'On the Optimizations board, click a recommendation, then “Open full details ↗” for the workspace — evidence, guidance, involved assets, and validation.',
      },
      {
        q: 'How is a saving “validated”?',
        a: 'After you execute, we re-measure the resource each sync and confirm the realized monthly saving (full or partial).',
      },
      {
        q: 'What do the categories mean?',
        a: 'Right-sizing (over-provisioned), Idle/Orphaned (unused), Storage tiering, Modernization (e.g. Arm), Networking, Commitments, Off-hours scheduling, Autoscaling (low baseline with recurring spikes), Reliability scale-ups, and AI / ML (idle or over-provisioned GPU and inference infrastructure).',
      },
      {
        q: 'Will Lizrd tell me to downsize a box I sized up for IOPS or network?',
        a: "No. Before recommending an idle or right-size downsize, Lizrd checks the instance's disk I/O and network throughput — not just CPU and memory — because those performance ceilings scale with instance size. An instance that looks idle on CPU but is pushing heavy disk I/O or network traffic (or is flagged at its disk ceiling) is held back from a downsize, so we won't suggest a change that would throttle it. This works across AWS, GCP, and Azure using metrics already collected — no extra permissions.",
      },
      {
        q: 'How does Lizrd handle occasional traffic spikes (e.g. a monthly campaign)?',
        a: "It tells a rare burst apart from a recurring one, so a spike or two a month doesn't distort a recommendation. Lizrd records each resource's true daily peak and looks at how often it recurs: a peak that happens on only a handful of days is treated as a burst, while one that recurs on a large share of days is treated as real, needed capacity. So a box that idles most of the time but spikes occasionally is offered a right-size that keeps headroom (never a delete), with a note to pair it with autoscaling for the spikes — and it won't be permanently scaled up just for a rare burst. A resource that genuinely peaks most days is left at its size and, if it's saturating, flagged to scale up. This works across AWS, GCP, and Azure from metrics already collected; a brand-new connection uses the safe (conservative) behavior until about a week of daily-peak history builds up.",
      },
      {
        q: 'Does Lizrd recommend autoscaling?',
        a: 'Yes. When a resource runs well below capacity most of the time but reaches a high peak that recurs predictably (a low baseline with regular spikes), Lizrd recommends moving it to dynamic scaling rather than paying peak-sized capacity 24/7 — with cloud-specific guidance: an EC2 Auto Scaling group on AWS, a managed instance group (MIG) on GCP, a VM Scale Set on Azure, or service auto scaling for ECS. It suggests setting the minimum near the baseline and the maximum to cover the peak (plus a scheduled scale-out if the spikes are time-predictable). The estimated saving is a conservative view of the off-peak headroom — treat it as directional, since autoscaling is an architecture change you apply and tune. Autoscaling for Kubernetes (HPA) is on the roadmap.',
      },
      {
        q: 'Does Lizrd optimize managed databases (RDS)?',
        a: 'Yes — beyond idle and right-sizing, Lizrd flags RDS databases on gp2 storage and recommends upgrading to gp3: ~20% cheaper per GB with a higher performance floor, applied in place with no downtime. It uses the database metadata already collected — no extra permissions. More database levers (serverless migration, non-prod HA, provisioned-IOPS) are on the roadmap.',
      },
      {
        q: 'Does Lizrd optimize data warehouses (Redshift / BigQuery)?',
        a: 'Yes, on both AWS and GCP. Amazon Redshift clusters and Google BigQuery slot reservations are discovered as data warehouses with a monthly cost and utilization. An idle warehouse gets a “pause” recommendation (stops compute; data is preserved), an over-provisioned one gets a right-size recommendation — fewer Redshift nodes, or fewer BigQuery slots (snapped to valid increments) — and a steady, well-utilized on-demand warehouse gets a commitment recommendation (reserved nodes / slot commitment, ~30% off). For on-demand BigQuery (billed per TB scanned), it also flags projects that scan far more than they store — a sign that partitioning/clustering would cut cost. It reads only cluster/reservation metadata and scanned/stored volumes and utilization (one read-only Redshift permission in the CloudFormation stack; GCP needs nothing extra) — never your table data.',
      },
      {
        q: 'What is implementation guidance?',
        a: 'On a recommendation’s details, click “Generate implementation guidance” for the exact steps, a diff to apply, what to watch for, and how to roll back.',
      },
      {
        q: 'Does Lizrd optimize serverless functions (Lambda / Cloud Functions)?',
        a: 'Yes, on both AWS and GCP. Functions get a real monthly cost from their memory, duration, and invocations, and over-provisioned functions get a memory right-size recommendation — on AWS grounded in Compute Optimizer, on GCP from the real used-memory metric in Cloud Monitoring. AWS x86 functions also get an Arm/Graviton migration (~20% cheaper compute; Arm isn’t offered for GCP Cloud Functions). And for AWS Lambda, under-used provisioned concurrency (always-warm capacity billed 24/7) is flagged to reduce or remove. To enable AWS memory right-sizing, opt into AWS Compute Optimizer and re-run the integration’s CloudFormation stack (it adds read-only permissions); GCP needs nothing extra.',
      },
      {
        q: 'Does Lizrd optimize AI and GPU infrastructure?',
        a: 'Yes, across AWS, GCP, and Azure. Lizrd discovers GPU instances and managed AI services — SageMaker & Bedrock, Vertex AI, and Azure ML & OpenAI — with a real monthly cost and GPU utilization, and finds the waste that hides in AI spend: idle GPU instances and inference endpoints (little or no traffic, billing 24/7), over-provisioned accelerators (right accelerator, right size — grounded in real GPU and GPU-memory usage), endpoints that should scale to zero between bursts of traffic, and interruptible batch/fine-tuning jobs that belong on cheaper Spot GPUs. Each finding carries the evidence, a confidence level, and the exact fix — read-only, and no change happens without your approval.',
      },
      {
        q: 'How does Lizrd see GPU utilization?',
        a: 'From your cloud’s own GPU metrics, read-only — on AWS via the CloudWatch agent’s GPU metrics, on GCP via the Ops Agent, and from managed-service metrics for inference endpoints. So an endpoint sitting at 1% GPU with no traffic, or a multi-GPU box running at 14%, is flagged with the measured evidence behind it, not a guess. Boxes without GPU telemetry yet are left alone until the signal is available.',
      },
      {
        q: 'Does Lizrd right-size Kubernetes workloads?',
        a: 'Yes — for EKS (GKE next), it finds Deployments/StatefulSets that request far more CPU/memory than they use and recommends lower requests (or scale-to-zero for idle ones), based on real usage vs request.',
      },
      {
        q: 'How do I turn on Kubernetes (EKS/GKE) discovery?',
        a: 'Open the cloud integration → “Kubernetes workloads” → Enable. By default Lizrd sets up the read-only cluster access for you (on AWS it manages its own EKS Access Entry + AWS view policy; on GCP the viewer service account’s roles/container.viewer grant covers it) — no kubectl step. Prefer to grant it yourself? Turn off “Set up access automatically” and we show the exact copy-paste snippet. Read-only and keyless — no cluster secret is stored.',
        to: '/domains',
      },
      {
        q: 'What access does Lizrd need for Kubernetes, and is it safe?',
        a: 'Read-only, no Secrets. Auto-provision grants an AWS-managed view policy (AWS) or roles/container.viewer (GCP), both of which exclude Secret contents; the manual path uses an even tighter get/list-only ClusterRole. Lizrd reads the cluster with short-lived tokens (no stored cluster credential) and, on AWS, can manage only its own access grant.',
      },
      {
        q: 'Will a Kubernetes recommendation point to the manifest in my repo?',
        a: 'Yes, once you connect the GitHub repo that holds the workload’s manifest. Lizrd links the running workload to its Deployment/StatefulSet manifest — by name, the app.kubernetes.io/name label, or the container image — even when Helm/Kustomize rename it, and shows the owning file plus a ready-to-apply resources.requests edit. If you run Argo CD or Flux, it uses their record of what came from where for an exact, template-proof link.',
        to: '/domains',
      },
    ],
  },
  {
    id: 'reviews',
    title: 'Pull-request reviews',
    icon: GitPullRequest,
    items: [
      {
        q: 'What are pull-request reviews?',
        a: 'When enabled, Lizrd reviews pull/merge requests that touch your infrastructure-as-code on GitHub & GitLab, and — only when it finds something worth flagging — comments the projected monthly cost delta and waste anti-patterns before they merge. PRs that don’t touch infrastructure are ignored, and a clean infra PR is listed on the Reviews page as “reviewed — no issues” without posting a comment. It’s read-only to your code.',
        to: '/domains',
      },
      {
        q: 'How is this different from a generic AI code review?',
        a: 'It’s grounded in your live knowledge graph — real resource usage, billed cost, and cloud↔code links — so it flags a concrete monthly $ change and waste specific to your infrastructure, not generic advice. That context is the moat: it knows how the resource you’re changing is actually used.',
      },
      {
        q: 'How do I turn reviews on, and who can?',
        a: 'Open a domain → Settings → Pull-request reviews and toggle it on (administrators & managers only). Everyone can then view findings on that domain’s Reviews page; scope which repositories, the minimum severity, and a cost-increase threshold right there.',
        to: '/domains',
      },
      {
        q: 'What does Lizrd flag in a review?',
        a: 'Four groups you can switch on or off per domain (Settings → Pull-request reviews → Comment types): (1) Cost increases — a projected monthly increase at or above your threshold; (2) Waste & anti-patterns — gp2 instead of gp3, over-provisioned IOPS, missing autoscaling, oversized requests, tightened HPAs; (3) Usage-aware checks — scaling up a resource that already runs cold, or shrinking one that looks idle on CPU but is working hard on disk/network I/O; (4) Recommendation cross-links — when a change touches a resource Lizrd already flags (unused/idle, a stranded commitment, a Graviton move, an off-hours schedule, or networking cost). Compliance and blocking gates aren’t part of this release.',
      },
      {
        q: 'Can I control which comments Lizrd makes?',
        a: 'Yes. Open a domain → Settings → Pull-request reviews → Comment types and toggle each group (Cost increases, Waste & anti-patterns, Usage-aware checks, Recommendation cross-links) on or off. Tune the noise further with the minimum severity and the cost-increase threshold right above it.',
        to: '/domains',
      },
      {
        q: 'Which infrastructure-as-code formats does the reviewer understand?',
        a: 'Terraform, CloudFormation (YAML & JSON), Kubernetes workload manifests, and Pulumi (TypeScript/JavaScript/Python). AWS CDK is reviewed via its synthesized CloudFormation template (cdk.out) when committed. Every changed resource is grounded in the same knowledge graph, so the cost/utilization/recommendation evidence is the same regardless of the language.',
      },
      {
        q: 'Why does the GitHub App ask for write permission?',
        a: 'Only so it can post on your pull requests when you enable Reviews — it needs Pull requests, Checks, and Commit statuses (write) to leave a review comment and report a pass/fail status before merge. It still never pushes, edits, or deletes your code, and reading for the knowledge graph stays read-only.',
      },
      {
        q: 'How do I enable reviews for GitLab?',
        a: 'Two things are needed: (1) an api-scoped token (a read-only read_api/read_repository token builds the graph but can’t comment — update it in place via the GitLab integration → “Update token”, no reconnect needed); and (2) a Group path on the integration — Lizrd registers the merge-request webhook at the GitLab group level, so the connection must be group-scoped (a real GitLab group, not a project path or your personal namespace). With both set and Reviews enabled, Lizrd registers the webhook for you automatically. If something’s missing, the Pull-request reviews settings page tells you exactly what.',
        to: '/domains',
      },
      {
        q: 'Where do I see review comments?',
        a: 'On each domain’s Reviews page. Pull requests are listed as rows; open one to see the findings grouped by code package, each with its severity, the cost/risk detail, and whether it was addressed.',
        to: '/domains',
      },
    ],
  },
  {
    id: 'compliance',
    title: 'NIS2 compliance',
    icon: ShieldCheck,
    items: [
      {
        q: 'What is the NIS2 compliance lens?',
        a: 'It reads the same read-only cloud connection you already use for cost and maps it to the NIS2 Art. 21 / §30 cloud-technical measures — a posture score, a ranked gap list, and guardrails on cost recommendations. It is evidence and continuous posture, not a certification, and not legal advice.',
        to: '/domains',
      },
      {
        q: 'How do I turn it on?',
        a: 'Open a domain → Compliance → “Enable compliance”. No new credentials or scopes are needed in this phase — it uses the cloud connection you already have.',
      },
      {
        q: 'What is a “compliance guardrail” on a recommendation?',
        a: 'Before you act on a cost saving, Lizrd checks whether it would genuinely weaken a resilience control — e.g. converting a database to Single-AZ (removing its failover), parking it on a schedule, or deleting a retained backup. A benign same-family downsize that keeps Multi-AZ is not flagged. When it would weaken a control, the recommendation shows a guardrail; click “Accept alternative” to switch the guidance to a compliant path (e.g. keep Multi-AZ and downsize one class instead) — the headline saving is re-estimated to match.',
      },
      {
        q: 'Can I export an evidence pack for the board or an auditor?',
        a: 'Yes — on the Compliance page click “Export evidence pack”. It opens a timestamped, data-resident document with your posture score, the enforced controls, every open gap, and a per-resource asset register, plus an honest scope disclaimer. Use “Print / Save as PDF”, or download it as JSON or CSV for a supplier questionnaire or an auditor’s spreadsheet.',
      },
      {
        q: 'Where do the gaps and the score come from?',
        a: 'Both are computed live from your knowledge graph each time you open the page — encryption, backups, failover, public exposure, supported versions and log retention across your resources. Findings are conservative: when a signal isn’t available for a resource, the control is marked not-applicable rather than failed.',
      },
      {
        q: 'Can I control which checks are enforced?',
        a: 'Yes — on the Compliance page, each control can be set to Block (flags cost actions that weaken it), Warn (advisory), or Off (not scored).',
      },
    ],
  },
  {
    id: 'integrations',
    title: 'Integrations',
    icon: Plug,
    items: [
      {
        q: 'My integration shows an error — what do I do?',
        a: 'Open the domain → Integrations → the integration, read the error, and re-run the setup/permissions. Health refreshes on the next sync.',
      },
      {
        q: 'How often can I trigger a manual sync?',
        a: 'Once every 15 minutes per integration. Open the integration and use “Sync now”; while you’re inside the cooldown the button is disabled and shows when it’s available again. Scheduled background syncs keep running regardless.',
      },
      {
        q: 'How do I connect my code (GitHub)?',
        a: 'Domain → Integrations → “Connect a source” → GitHub. Linking code sharpens recommendations and gives copy-paste fixes.',
      },
      {
        q: 'How many sources can I connect to a domain?',
        a: 'Up to 10 cloud accounts and 20 code bases per domain. Need more? Split them across additional domains, or disconnect one you no longer use.',
      },
      {
        q: 'What does “verified from state” mean?',
        a: 'The cloud↔code link was confirmed from your Terraform/CloudFormation state — an exact match, not a heuristic guess.',
      },
      {
        q: 'Why does my GCP account show “billing pending”, “billing not set up”, or estimated cost?',
        a: 'GCP exposes billed cost only through a BigQuery billing export (unlike AWS Cost Explorer / Azure Cost Management, which work automatically). “Billing not set up” = no export table configured yet. “Billing pending” = the export is set but hasn’t produced data — GCP creates and backfills the table within a few hours of enabling the export, so this clears on its own (we keep re-checking). To set it up: run the GCP setup script (it provisions the dataset + permissions), then in Console → Billing → Billing export → BigQuery export, enable “Standard usage cost” pointed at the lizrd_billing dataset (needs Billing Account Administrator), and paste the printed table into the integration. Until billed cost arrives, GCP cost is shown as estimates (fine for optimization). Admins can hit “Re-check billing now” on the integration to refresh immediately.',
      },
      {
        q: 'How do I connect my Terraform state for AWS, GCP, or Azure?',
        a: 'You can point Lizrd at your terraform.tfstate for any cloud, either while connecting (the setup dialog has an “Advanced → Terraform state” section with a “Test read” button) or anytime afterwards from the source’s detail panel (Domain → Integrations → click the source). Enter the state location — AWS: S3 bucket + key (plus the KMS key ARN if it’s SSE-KMS); GCP: GCS bucket + object; Azure: storage account + container + blob key — then Test read and Save. It’s read-only and the state contents are never stored; connecting it upgrades cloud↔code links to exact “verified from state” matches.',
      },
    ],
  },
  {
    id: 'members',
    title: 'Members & access',
    icon: Users,
    items: [
      {
        q: 'How do I invite a teammate?',
        a: 'Go to Members and invite by email; they join with the role you choose. When you invite an Engineer or Viewer you can tick the domains they should access right in the invite form — access is granted the moment they accept. An organization can have up to 100 users (billing is based on cloud spend under management, not seats).',
        to: '/members',
      },
      {
        q: 'How do I give someone access to specific domains?',
        a: 'On Members, each Engineer or Viewer shows the domains they can reach; click “Manage domains” to tick or untick domains and Save — a single dialog sets their whole access list. (You can also do it per-domain from the domain’s Settings → Manage Access.) Administrators and Managers reach every domain automatically, so they have no per-domain toggle.',
        to: '/members',
      },
      {
        q: 'What can each role do?',
        a: 'Admins and Managers manage the organization and recommendations and reach every domain; Engineers act on recommendations and can generate deeper AI explanations; Viewers have read-only access. Engineers and Viewers only see the domains they’ve been granted.',
      },
      {
        q: 'What is the Activity page?',
        a: 'An admin overview of two things: domain health (is every source syncing cleanly, and when did it last sync) and team activity (each member’s role, access, 2FA, and last login). Domains that need attention sort to the top.',
        to: '/activity',
      },
    ],
  },
  {
    id: 'account',
    title: 'Account & settings',
    icon: Settings,
    items: [
      {
        q: 'How do I change my notification preferences?',
        a: 'Open your profile menu → Profile, and adjust the notification toggles.',
        to: '/profile',
      },
      {
        q: 'What does the notification bell show?',
        a: 'Unread notifications (until you open or dismiss them) plus anything from the last 7 days — newest first, with the time each arrived. Older, already-read items age out of the bell automatically, so it stays focused on what’s new and recent. Use “Mark all read” to clear the count, or “×” to dismiss one.',
      },
      {
        q: 'What is the weekly optimization digest?',
        a: 'A Monday email summarizing your week — new recommendations, savings validated last week, and your standing backlog — scoped to just the domains you can access, with links straight to the action. Turn it off (or send yourself a preview) under Profile → Email notifications.',
        to: '/profile',
      },
      {
        q: 'How do I turn the savings leaderboard on or off?',
        a: 'Open the domain → Settings and toggle “Show leaderboard.”',
      },
      {
        q: 'How does Lizrd pricing work?',
        a: 'You pay a flat monthly fee based on your spend under management — the real cloud cost across your connected accounts — in simple bands, after a 7-day free trial. See it all on the Billing page.',
        to: '/settings/billing',
      },
      {
        q: 'What happens when my free trial ends?',
        a: 'You get the full product free for 7 days from signup — no card required to start. When the trial ends, add a payment method to keep going; we’ll show you how many optimizations are waiting and your monthly savings potential, and one click takes you to secure Stripe checkout. Your data and recommendations are kept — nothing is deleted.',
        to: '/settings/billing',
      },
      {
        q: 'What if my spend crosses a band partway through the month?',
        a: 'Your band is set by your total spend under management for the whole month, settled at month close and billed in arrears — so mid-month changes are counted for exactly the days they applied. Add accounts and your total (and band) can rise; remove them and it falls. During the month the Billing page shows a clearly-labelled projection (“so far → projected”); the number you’re actually billed is the settled month-end total.',
        to: '/settings/billing',
      },
      {
        q: 'Do I get a statement each month?',
        a: 'Yes. When a month closes we freeze it into a statement — the settled spend, your band, any trial credit, and the amount due — email it to your billing contact, and list it under Statements on the Billing page. Frozen means the figures never change afterwards, even if our pricing later does.',
        to: '/settings/billing',
      },
    ],
  },
  {
    id: 'security',
    title: 'Security & data',
    icon: Lock,
    items: [
      {
        q: 'How do I delete my account?',
        a: 'Settings → the “Danger zone” at the bottom (administrators only) → “Delete account”, then type your organization name to confirm. This is irreversible: your org is deactivated, every integration is disconnected, and everyone loses access. We issue a final bill for the current month’s cloud spend. Signing up again later with the same email starts a brand-new account.',
        to: '/settings',
      },
      {
        q: 'How do I export my data?',
        a: 'Two options. Profile → “Your data” → “Export your data” downloads just your own personal data (your profile, the comments and activity you authored, notifications, sign-in links) — available to everyone. Administrators can also export the whole organization’s data from Settings → “Your data”. Both download as JSON and never include secrets or credentials.',
        to: '/profile',
      },
      {
        q: 'How do I turn on two-factor authentication (2FA)?',
        a: 'Open Profile → Two-factor authentication → “Set up authenticator app,” scan the QR with an authenticator app, enter the code, and save your recovery codes.',
        to: '/profile',
      },
      {
        q: 'I lost my authenticator device — how do I get in?',
        a: 'On the sign-in code step, enter one of the recovery codes you saved during setup (each works once). Then set up a new authenticator from your profile.',
        to: '/profile',
      },
      {
        q: 'Can I require two-factor authentication for everyone?',
        a: 'Yes — an admin can set Settings → Security → Two-factor authentication to “Required.” Password users are then prompted to enroll on their next sign-in; Google/GitHub members are already covered.',
        to: '/settings',
      },
      {
        q: 'Can I use a passkey or security key instead of a code?',
        a: 'Yes — go to Profile → Two-factor authentication → “Add a passkey” (Touch ID, Windows Hello, or a hardware key). At sign-in, choose “Use a passkey” instead of typing a code.',
        to: '/profile',
      },
      {
        q: 'A teammate is locked out of two-factor — how do I help?',
        a: 'An admin can open Members, find them, and click “Reset 2FA.” That clears their authenticator/passkeys/recovery codes so they can sign in with their password and set two-factor up again.',
        to: '/members',
      },
      {
        q: 'Is my data safe?',
        a: 'Access is read-only and least-privilege, encrypted and isolated to your workspace. We never modify your infrastructure, and you can revoke access anytime.',
      },
      {
        q: 'What data does Lizrd collect?',
        a: 'Resource configuration, cost, and utilization metrics, plus code/IaC needed to correlate and explain recommendations — never your application data.',
      },
      {
        q: 'Do my resource, repository, or file names get sent to the AI?',
        a: 'No. By default Lizrd tokenizes names out of every AI prompt and restores them only in your own view — the model sees placeholders, you see the real thing. Identifying tag values are also one-way hashed before they’re stored. This is on for everyone, with no setup.',
      },
      {
        q: 'Can I reduce what Lizrd stores and sends to the AI even further?',
        a: 'Yes — an admin can open Settings → Privacy and turn on any of three controls: “Ingest source code” off (Lizrd never stores or fetches your code; “Guide me” fixes stay prose-only), “AI resource correlation” off (resource names aren’t sent to the AI for name-based matching; rule-based matching still runs), and “Tokenize resource names” (Lizrd stores only a pseudonym for each resource name, never the real name). Each trades a little precision for more privacy.',
        to: '/settings',
      },
      {
        q: 'If I turn a privacy control off, what happens to data already collected?',
        a: 'It’s removed retroactively, not just going forward. Turning off source ingestion permanently purges stored source snippets and source-grounded AI content; turning off AI correlation removes the AI-matched resource→code links already in your graph. You’ll see a summary of exactly what was removed, and it can’t be undone.',
        to: '/settings',
      },
      {
        q: 'What does “Tokenize resource names” do, and can I undo it?',
        a: 'When on, Lizrd replaces every resource name with a salted pseudonym (e.g. res_7f3a9c2e) the moment data arrives and permanently purges the names it already held — the real names never reach us. It is a one-way door: it cannot be turned back off. Recommendations then identify resources by pseudonym, so you locate them in your own cloud console by their resource ID, and our support can no longer help you by name. Because it’s irreversible, enabling it requires typing your organization’s name to confirm.',
        to: '/settings',
      },
      {
        q: 'Where is my data stored? Can I keep it in the EU?',
        a: 'Yes. Lizrd runs a separate stack per region — US (N. Virginia) and EU (Frankfurt) — and your data stays in the region you choose, never leaving it. Pick your region right on the signup/login screen (it’s pre-selected from your location); it’s locked in once you create your account. You can see your current region any time in Settings → Data residency.',
        to: '/settings',
      },
    ],
  },
];
